import styled, { css, keyframes } from 'styled-components';
import theme from 'theme/theme';
import media from 'utils/MediaQueries';

/**
 * Single page geometry. Every full-width band on the marketing pages uses these
 * so the content edges line up in one vertical column down the whole page —
 * hero, catalogue and contact previously each had their own max-width and
 * padding, so nothing aligned.
 */
const PAGE_MAX = '1200px';
const PAGE_PAD = '20px';
const PAGE_PAD_MOBILE = '20px';
const GUTTER = '108px';
const GUTTER_GAP = '40px';

/**
 * Section primitives shared by the landing and Why Flass pages.
 *
 * The conceit is a module catalogue — the artifact a university already
 * publishes to describe what it offers. Entries carry a coded identifier whose
 * prefix names the group it belongs to (MSG, TCH, SEC…), a spine runs down the
 * left like the tabbed edge of a printed prospectus, and the utility face is a
 * monospace because catalogues, timetables and registration systems are all set
 * in tabular type.
 *
 * Keep new marketing pages on these rather than re-implementing a section.
 */

/**
 * Entrance is CSS-only and additive: content is visible by default and the
 * animation only ever moves it. react-reveal gated visibility on a scroll
 * listener that frequently never fired, leaving most of both pages stuck at
 * opacity 0 for real visitors.
 */
const rise = keyframes`
	from { opacity: 0; transform: translateY(14px); }
	to   { opacity: 1; transform: none; }
`;

const riseIn = css`
	animation: ${rise} 620ms cubic-bezier(0.22, 0.68, 0.28, 1) both;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

const Section = styled.section`
	width: 100%;
	display: flex;
	justify-content: center;
	padding: 104px 0;
	background-color: ${(props) => props.$alt && theme.background2};

	${media.tablet`
		padding: 62px 0;
	`};
`;

/**
 * Mirrors the TopBar exactly (max-width 1200 with 20px padding inside it), so
 * section content begins on the same column as the Flass logo.
 */
const SectionInner = styled.div`
	width: 100%;
	max-width: ${PAGE_MAX};
	padding: 0 ${PAGE_PAD};

	${media.tablet`
		padding: 0 ${PAGE_PAD_MOBILE};
	`};
`;

const SectionBody = styled.div`
	min-width: 0;
	${riseIn};
`;

const SectionHeader = styled.div`
	max-width: 620px;
	margin-bottom: 52px;

	${media.tablet`
		margin-bottom: 34px;
	`};
`;

const SectionEyebrow = styled.span`
	display: block;
	font-family: ${theme.monoFont};
	color: ${theme.primary1};
	font-size: 12px;
	font-weight: 500;
	letter-spacing: 2px;
	text-transform: uppercase;
	margin-bottom: 15px;
`;

const SectionTitle = styled.h2`
	font-family: ${theme.headerFont};
	color: ${theme.chalk};
	font-size: 33px;
	line-height: 1.24;
	margin: 0 0 15px 0;

	${media.phone`
		font-size: 25px;
	`};
`;

const SectionSubtitle = styled.p`
	margin: 0;
	font-size: 16.5px;
	line-height: 1.68;
	color: ${theme.chalk};
	opacity: 0.7;
`;

/**
 * Entries are laid out like sessions on a university timetable: each one is a
 * block with a coloured spine down its leading edge, sitting on a faintly
 * raised surface. The previous treatment — bare text separated by hairline
 * rules — is the newspaper look that every generic layout reaches for, and it
 * gave the eye nothing to land on when scanning twenty-odd capabilities.
 *
 * The surface is a translucent white so a block reads correctly on both the
 * base and alternate section backgrounds without needing two variants.
 */
const Grid = styled.div`
	display: grid;
	/* $three is for short ordered sets — a row of three reads as a sequence,
	   where three items in a two-column grid leaves an orphan. */
	grid-template-columns: repeat(
		${(props) => (props.$three ? 3 : 2)},
		minmax(0, 1fr)
	);
	gap: 18px;

	${media.tablet`
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 14px;
	`};

	${media.phone`
		grid-template-columns: 1fr;
	`};
`;

const Item = styled.div`
	background-color: rgba(255, 255, 255, 0.035);
	border-left: 3px solid ${theme.primary1};
	/* Matches the rounded language of the logo tile and buttons rather than the
	   zero-radius broadsheet default. */
	border-radius: 6px;
	padding: 22px 24px 24px 21px;

	${media.phone`
		padding: 18px 18px 20px 15px;
	`};
`;

const ItemTitle = styled.h3`
	color: ${theme.chalk};
	font-size: 16px;
	font-weight: 700;
	letter-spacing: 0.1px;
	margin: 0 0 8px 0;
`;

const ItemText = styled.p`
	margin: 0;
	font-size: 14.5px;
	line-height: 1.7;
	color: ${theme.chalk};
	opacity: 0.66;
`;

const PlatformNote = styled.p`
	max-width: 600px;
	margin: 44px 0 0 0;
	font-family: ${theme.monoFont};
	font-size: 13px;
	line-height: 1.75;
	color: ${theme.chalk};
	opacity: 0.55;
`;

export {
	PAGE_MAX,
	PAGE_PAD,
	PAGE_PAD_MOBILE,
	GUTTER,
	GUTTER_GAP,
	Section,
	SectionInner,
	SectionBody,
	SectionHeader,
	SectionEyebrow,
	SectionTitle,
	SectionSubtitle,
	Grid,
	Item,
	ItemTitle,
	ItemText,
	PlatformNote,
	riseIn,
};
